import { z } from "zod";
import { PILL_COLORS } from "../../lib/colorPill";
import { numberFormat as cachedNumberFormat } from "../intl";
import {
  fail,
  ok,
  type AggregateId,
  type FieldKind,
  type FilterOperator,
} from "../kind";
import type { FieldSettingsMap } from "../types";

export const numberSettings = z.object({
  // Un seul type numérique, comme Notion : le format ne change que
  // l'affichage, jamais la valeur. Un pourcentage se stocke en fraction
  // (0,42 s'affiche 42 %).
  format: z.enum(["number", "percent", "currency"]).default("number"),
  // Code ISO 4217.
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .default("EUR"),
  currencyDisplay: z.enum(["symbol", "code", "name"]).default("symbol"),
  // Symbole avant ou après la valeur ; `auto` suit la locale du lecteur.
  currencyPosition: z.enum(["auto", "before", "after"]).default("auto"),
  // Unité libre (kg, h, km…), hors devise et pourcentage.
  unit: z.string().max(12).optional(),
  unitPosition: z.enum(["before", "after"]).default("after"),
  // Absent : autant de décimales que nécessaire (jusqu'à 8).
  decimals: z.number().int().min(0).max(8).optional(),
  thousandsSeparator: z.boolean().default(true),
  // 1,2 k, 3,4 M.
  abbreviate: z.boolean().default(false),
  min: z.number().optional(),
  max: z.number().optional(),
  step: z.number().positive().optional(),
  display: z.enum(["value", "bar", "ring", "slider"]).default("value"),
  // Une couleur de la palette des pastilles (cf. convex/lib/colorPill.ts).
  barColor: z.enum(PILL_COLORS).default("blue"),
  showValue: z.boolean().default(true),
});

type NumberSettings = FieldSettingsMap["number"];

const ABBREVIATIONS: Record<string, number> = {
  k: 1e3,
  m: 1e6,
  md: 1e9,
  mds: 1e9,
  b: 1e9,
  g: 1e9,
  t: 1e12,
};

/** Le séparateur décimal de la locale : "," en français, "." en anglais. */
function decimalSeparator(locale: string): string {
  return (
    cachedNumberFormat(locale)
      .formatToParts(1.5)
      .find((part) => part.type === "decimal")?.value ?? "."
  );
}

/**
 * Un nombre écrit par un humain, un tableur ou un LLM : « 1 234,5 »,
 * « 1,234.5 », « 12 % », « 1,5 k », « 12 € », « 3 kg ». Le pourcentage
 * divise par 100 : « 42 % » vaut 0,42.
 */
export function parseNumberText(
  input: string,
  locale: string,
  unit?: string,
): number | null {
  let text = input.trim().toLowerCase();
  if (text === "") return null;

  let negative = false;
  if (/^\(.*\)$/.test(text)) {
    negative = true;
    text = text.slice(1, -1).trim();
  }

  let factor = 1;
  if (text.endsWith("%")) {
    factor = 0.01;
    text = text.slice(0, -1).trim();
  }
  if (unit) {
    const u = unit.toLowerCase();
    if (text.endsWith(u)) text = text.slice(0, -u.length).trim();
    else if (text.startsWith(u)) text = text.slice(u.length).trim();
  }
  // Symboles et codes de devise, devant ou derrière.
  text = text
    .replace(/^[\p{Sc}]\s*/u, "")
    .replace(/\s*[\p{Sc}]$/u, "")
    .replace(/^[a-z]{3}\s+/, "")
    .replace(/\s+[a-z]{3}$/, "");

  const abbreviation = /^(.*?)\s*(mds|md|k|m|b|g|t)$/.exec(text);
  if (abbreviation && /\d$/.test(abbreviation[1]!)) {
    factor *= ABBREVIATIONS[abbreviation[2]!]!;
    text = abbreviation[1]!;
  }

  if (text.startsWith("-") || text.startsWith("−")) {
    negative = !negative;
    text = text.slice(1).trim();
  } else if (text.startsWith("+")) {
    text = text.slice(1).trim();
  }

  // Séparateurs de milliers : espaces (y compris insécables) et apostrophes.
  text = text.replace(/[\s\u00a0\u202f']/g, "");
  if (!/^[\d.,]+$/.test(text) || !/\d/.test(text)) return null;

  const lastComma = text.lastIndexOf(",");
  const lastDot = text.lastIndexOf(".");
  let normalized: string;
  if (lastComma >= 0 && lastDot >= 0) {
    // Les deux : le dernier est la décimale.
    const decimal = lastComma > lastDot ? "," : ".";
    const thousands = decimal === "," ? "." : ",";
    normalized = text.split(thousands).join("").replace(decimal, ".");
  } else if (lastComma >= 0 || lastDot >= 0) {
    const sep = lastComma >= 0 ? "," : ".";
    const parts = text.split(sep);
    const groupsOfThree =
      parts.length > 1 && parts.slice(1).every((part) => part.length === 3);
    if (parts.length > 2) {
      // « 1.234.567 » : forcément des milliers.
      if (!groupsOfThree) return null;
      normalized = parts.join("");
    } else if (groupsOfThree && sep !== decimalSeparator(locale)) {
      // « 1,234 » en anglais, « 1.234 » en français : des milliers.
      normalized = parts.join("");
    } else {
      normalized = parts.join(".");
    }
  } else {
    normalized = text;
  }

  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;
  const result = (negative ? -value : value) * factor;
  // 0,1 + 0,2 : on arrondit le bruit des flottants introduit par le facteur.
  return Number(result.toPrecision(15));
}

function numberFormat(settings: NumberSettings, locale: string, plain: boolean) {
  const fraction =
    settings.decimals !== undefined
      ? { minimumFractionDigits: settings.decimals, maximumFractionDigits: settings.decimals }
      : { maximumFractionDigits: 8 };
  if (plain) {
    // Sans perte et relisible : pas d'abréviation ni d'arrondi.
    return cachedNumberFormat(locale, {
      style: settings.format === "percent" ? "percent" : "decimal",
      maximumFractionDigits: 10,
      useGrouping: false,
    });
  }
  return cachedNumberFormat(locale, {
    style: settings.format === "number" ? "decimal" : settings.format,
    ...(settings.format === "currency"
      ? { currency: settings.currency, currencyDisplay: settings.currencyDisplay }
      : {}),
    ...fraction,
    useGrouping: settings.thousandsSeparator,
    notation: settings.abbreviate ? "compact" : "standard",
  });
}

export function formatNumber(
  value: number,
  settings: NumberSettings,
  locale: string,
  style: "display" | "plain",
): string {
  if (style === "plain") {
    const text = numberFormat(settings, locale, true).format(value);
    if (settings.format === "currency") return `${text} ${settings.currency}`;
    if (settings.unit && settings.format === "number") {
      return settings.unitPosition === "before"
        ? `${settings.unit} ${text}`
        : `${text} ${settings.unit}`;
    }
    return text;
  }

  const formatter = numberFormat(settings, locale, false);
  let text: string;
  if (settings.format === "currency" && settings.currencyPosition !== "auto") {
    const parts = formatter.formatToParts(value);
    const currency = parts
      .filter((part) => part.type === "currency")
      .map((part) => part.value)
      .join("");
    const rest = parts
      .filter((part) => part.type !== "currency")
      .map((part) => part.value)
      .join("")
      .trim();
    text =
      settings.currencyPosition === "before"
        ? `${currency}${currency.length > 1 ? " " : ""}${rest}`
        : `${rest} ${currency}`;
  } else {
    text = formatter.format(value);
  }
  if (settings.unit && settings.format === "number") {
    text =
      settings.unitPosition === "before"
        ? `${settings.unit} ${text}`
        : `${text} ${settings.unit}`;
  }
  return text;
}

function compareOperator(
  id: "eq" | "neq" | "gt" | "gte" | "lt" | "lte",
  test: (a: number, b: number) => boolean,
): FilterOperator<"number", number> {
  return { id, operand: "value", matchesEmpty: id === "neq", test: (v, o) => test(v, o) };
}

export const NUMERIC_AGGREGATES: AggregateId[] = [
  "count",
  "count_empty",
  "count_not_empty",
  "count_unique",
  "percent_empty",
  "percent_not_empty",
  "sum",
  "average",
  "median",
  "min",
  "max",
  "range",
];

export const numberKind: FieldKind<"number"> = {
  type: "number",
  settings: numberSettings,
  value: z.number().refine(Number.isFinite, "Number must be finite."),
  coerce(input, settings, ctx) {
    let value: number | null;
    if (typeof input === "number") {
      value = Number.isFinite(input) ? input : null;
      if (value === null) return fail("Number must be finite.");
    } else if (typeof input === "string") {
      value = parseNumberText(input, ctx.locale, settings.unit);
      if (value === null) return fail(`"${input}" is not a number.`);
    } else {
      return fail("Expected a number.");
    }
    if (settings.min !== undefined && value < settings.min) {
      return fail(`Number must be at least ${settings.min}.`);
    }
    if (settings.max !== undefined && value > settings.max) {
      return fail(`Number must be at most ${settings.max}.`);
    }
    return ok(value);
  },
  format: (value, settings, ctx) => formatNumber(value, settings, ctx.locale, ctx.style),
  compare: (a, b) => a - b,
  filters: [
    compareOperator("eq", (a, b) => a === b),
    compareOperator("neq", (a, b) => a !== b),
    compareOperator("gt", (a, b) => a > b),
    compareOperator("gte", (a, b) => a >= b),
    compareOperator("lt", (a, b) => a < b),
    compareOperator("lte", (a, b) => a <= b),
  ],
  aggregates: NUMERIC_AGGREGATES,
  toNumber: (value) => value,
};
